import type { PlanningRow } from '@crm/shared'
import { PlanningAmountInput } from './PlanningAmountInput'
import {
  MULTI_MONTH_READONLY_TITLE,
  PLAN_READONLY_TITLE,
  ROLE_LABELS,
  formatFactDate,
  percent,
  percentColorClass,
} from './planningFormat'

interface AkbTableProps {
  rows: PlanningRow[]
  /** Подпись периода под колонками (единая с основной таблицей). */
  periodLabel: string
  /** Период из нескольких месяцев — суммы только для просмотра. */
  multiMonth: boolean
  /** id менеджера, чья строка только для чтения (РОП не правит свой АКБ). */
  readonlyManagerId?: string | null
  /** true — вся таблица в режиме просмотра (Генеральный директор, помощник). */
  readOnlyAll?: boolean
  /** true — план только для чтения, факт правится (бухгалтер). */
  planReadOnly?: boolean
  /** true — сохранено, false — сервер отказал (тост показан в usePlanningUpsert). */
  onPatch: (managerId: string, akbPlanCount?: number, akbFactCount?: number) => Promise<boolean>
}

/**
 * Таблица «АКБ» (Активная Клиентская База).
 *
 * Аналогична основной финансовой таблице, но:
 *   - Без чекбоксов (не идёт в график на дашборде).
 *   - Без колонок «КП выставлено» / «В заказ» — это финансовые показатели.
 *   - План/факт — целые числа клиентов (штуки), не деньги.
 *   - Итоги по столбцам + % суммарный.
 *
 * Как и в основной таблице, править можно только в режиме «Месяц»: сумма за
 * квартал или год записалась бы в его первый месяц.
 */
export function AkbTable({
  rows,
  periodLabel,
  multiMonth,
  readonlyManagerId = null,
  readOnlyAll = false,
  planReadOnly = false,
  onPatch,
}: AkbTableProps) {
  const totalPlan = rows.reduce((s, r) => s + r.akbPlanCount, 0)
  const totalFact = rows.reduce((s, r) => s + r.akbFactCount, 0)

  return (
    <div className="space-y-2">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">АКБ</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Активная клиентская база — количество клиентов, план и факт. Цифры — штуки.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Менеджер</th>
                <th className="px-3 py-2 text-right w-36">
                  План, шт
                  <div className="text-[10px] text-gray-400 font-normal">{periodLabel}</div>
                </th>
                <th className="px-3 py-2 text-right w-36">
                  Факт, шт
                  <div className="text-[10px] text-gray-400 font-normal">{periodLabel}</div>
                </th>
                <th className="px-3 py-2 text-right w-16">%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <AkbRowView
                  key={row.managerId}
                  row={row}
                  readonly={readOnlyAll || multiMonth || row.managerId === readonlyManagerId}
                  readonlyTitle={multiMonth ? MULTI_MONTH_READONLY_TITLE : undefined}
                  planReadonly={planReadOnly}
                  onPatch={(plan, fact) => onPatch(row.managerId, plan, fact)}
                />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                    Менеджеров не найдено
                  </td>
                </tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-50 text-gray-700 font-medium">
                <tr>
                  <td className="px-3 py-2 text-right">Итого:</td>
                  <td className="px-3 py-2 text-right">{totalPlan.toLocaleString('ru-RU')}</td>
                  <td className="px-3 py-2 text-right">{totalFact.toLocaleString('ru-RU')}</td>
                  <td className={`px-3 py-2 text-right ${percentColorClass(totalPlan, totalFact)}`}>
                    {percent(totalPlan, totalFact)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {multiMonth && (
        <p className="text-xs text-gray-400">
          В таблице сумма за период. Править АКБ можно только в режиме «Месяц».
        </p>
      )}
    </div>
  )
}

interface AkbRowProps {
  row: PlanningRow
  readonly?: boolean
  readonlyTitle?: string
  planReadonly?: boolean
  onPatch: (akbPlanCount?: number, akbFactCount?: number) => Promise<boolean>
}

function AkbRowView({
  row,
  readonly = false,
  readonlyTitle,
  planReadonly = false,
  onPatch,
}: AkbRowProps) {
  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-3 py-3 align-top">
        <div className="font-medium text-gray-900">{row.managerName}</div>
        <div className="text-xs text-gray-400">
          {ROLE_LABELS[row.role] ?? row.role} · {row.managerEmail}
        </div>
      </td>
      <td className="px-3 py-3 text-right align-top">
        <PlanningAmountInput
          value={row.akbPlanCount}
          kind="count"
          label={`План АКБ, шт — ${row.managerName}`}
          readonly={readonly || planReadonly}
          readonlyTitle={readonlyTitle ?? (planReadonly ? PLAN_READONLY_TITLE : undefined)}
          onSave={(v) => onPatch(v, undefined)}
        />
      </td>
      <td className="px-3 py-3 text-right align-top">
        <PlanningAmountInput
          value={row.akbFactCount}
          kind="count"
          label={`Факт АКБ, шт — ${row.managerName}`}
          readonly={readonly}
          readonlyTitle={readonlyTitle}
          onSave={(v) => onPatch(undefined, v)}
        />
        <div className="mt-1 text-[10px] text-gray-400 whitespace-nowrap">
          {formatFactDate(row.akbFactUpdatedAt)}
        </div>
      </td>
      <td
        className={`px-3 py-3 pt-4 text-right tabular-nums align-top ${percentColorClass(row.akbPlanCount, row.akbFactCount)}`}
      >
        {percent(row.akbPlanCount, row.akbFactCount)}
      </td>
    </tr>
  )
}
